// CreateHospital.tsx
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  requiredValidator,
  emailWOValueValidator,
  phoneValidator,
} from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createHospital,
  getHospitalByID,
  getHospitalImagesByID,
  updateHospital,
  uploadHospitalMultipleImages,
} from "./services/hospital.service";
import { clearHospitalDetails } from "./hospitalSlice";
import { getFile, slugify } from "../../_helper/helper";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import { getAllActiveCities } from "../city/services/city.services";
import { getAllActiveStates } from "../state/services/state.services";
import { getAllActiveService } from "../service/services/service.services";
import { getAllActiveSpecialties } from "../specialties/services/specialties.service";
import { getAllActiveFacility } from "../facility/services/facility.service";
import { findIndex } from "lodash";
import HospitalImageUpload from "./HospitalImageForm";
import HospitalRatingsArray from "./HospitalRatingsArray";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import { getAllActiveHospitalTypes } from "../hospitaltype/services/hospitalType.service";
import FormRichTextField from "../../components/formFields/FormRichTextField";

interface StateChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const StateChangeWatcher: React.FC<StateChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const cityID = formRenderProps.valueGetter("CityID");
  const cities = useAppSelector((state) => state.city?.CityList);

  useEffect(() => {
    const CityID = cityID ? cityID : undefined;
    const stateIndex = findIndex(cities, { ID: CityID });
    if (stateIndex > -1) {
      const city = cities[stateIndex];
      formRenderProps.onChange("StateID", { value: city?.StateID });
    } else {
      formRenderProps.onChange("StateID", { value: "" });
      formRenderProps.onChange("CityID", { value: "" });
    }
  }, [cityID]);
  return null;
};

const CreateHospital: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const HospitalID = location.state?.HospitalID;
  const HospitalDetail = useAppSelector(
    (state) => state.hospital.HospitalDetail,
  );
  const HospitalTypeList = useAppSelector(
    (state) => state.hospitalType.HospitalTypeList,
  );
  const loading = useAppSelector((state) => state.hospital.formLoading);
  const cities = useAppSelector((state) => state.city?.CityList || []);
  const states = useAppSelector((state) => state.state?.StateList || []);
  const services = useAppSelector((state) => state.service?.ServiceList || []);
  const specialties = useAppSelector(
    (state) => state.specialty?.SpecialtyList || [],
  );
  const facilities = useAppSelector(
    (state) => state.facility?.FacilityList || [],
  );
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [HospitalDetail]);

  useEffect(() => {
    dispatch(getAllActiveHospitalTypes());
    dispatch(getAllActiveCities());
    dispatch(getAllActiveStates());
    dispatch(getAllActiveService());
    dispatch(getAllActiveSpecialties());
    dispatch(getAllActiveFacility());

    if (HospitalID) {
      dispatch(getHospitalByID(HospitalID));
      dispatch(getHospitalImagesByID(HospitalID));
    }
    return () => {
      dispatch(clearHospitalDetails());
    };
  }, [HospitalID]);

  const handleSubmit = async (values: any) => {
    // Convert arrays to comma-separated strings for API
    const specialtiesArray = Array.isArray(values.Specialties)
      ? values.Specialties.join(",")
      : values.Specialties || "";

    const facilitiesArray = Array.isArray(values.Facilities)
      ? values.Facilities.join(",")
      : values.Facilities || "";

    const servicesArray = Array.isArray(values.AvailableSevice)
      ? values.AvailableSevice.join(",")
      : values.AvailableSevice || "";

    const ImageData = new FormData();
    const MainImage =
      values?.MainImage && typeof values?.MainImage !== "string"
        ? getFile(values?.MainImage[0])
        : "";
    const RegistrationCertificateImage =
      values?.RegistrationCertificateImage &&
      typeof values?.RegistrationCertificateImage !== "string"
        ? getFile(values?.RegistrationCertificateImage[0])
        : "";
    const AccreditationCertificateImage =
      values?.AccreditationCertificateImage &&
      typeof values?.AccreditationCertificateImage !== "string"
        ? getFile(values?.AccreditationCertificateImage[0])
        : "";

    // Prepare payload matching API schema
    const insertPayload = {
      ID: HospitalID || 0,
      HospitalName: values.HospitalName ?? "",
      HospitalTypeID: values.HospitalTypeID ?? null,
      RegistrationNo: values.RegistrationNo ?? "",
      Accreditations: values.Accreditations ?? "",
      Url: values.Url ?? "",
      TagLine: values.TagLine ?? "",
      Address: values.Address ?? "",
      Area: values.Area ?? "",
      CityID: values.CityID ?? 0,
      StateID: values.StateID ?? 0,
      MobileNo: values.MobileNo ?? "",
      EmailID: values.EmailID ?? "",
      Website: values.Website ?? "",
      MapURL: values.MapURL ?? "",
      EstablishedYear: values.EstablishedYear ? values.EstablishedYear : 0,
      AboutHospital: values.AboutHospital ?? "",
      TotalBed: values.TotalBed ? Number(values.TotalBed) : 0,
      TotalDoctors: values.TotalDoctors ? Number(values.TotalDoctors) : 0,
      OPDTime: values.OPDTime ?? "",
      IsEmergencyAvailable: values.IsEmergencyAvailable ?? false,
      AvailableSevice: servicesArray,
      Specialties: specialtiesArray,
      Facilities: facilitiesArray,
      IsActive: values.IsActive === 1,
    };

    try {
      if (HospitalID) {
        const response = await dispatch(updateHospital(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.MainImage && typeof values?.MainImage !== "string") ||
            (values?.RegistrationCertificateImage &&
              typeof values?.RegistrationCertificateImage !== "string") ||
            (values?.AccreditationCertificateImage &&
              typeof values?.AccreditationCertificateImage !== "string") ||
            values.attachment?.length > 0
          ) {
            ImageData.append("HospitalID", HospitalID);
            ImageData.append("MainImage", MainImage);
            ImageData.append(
              "RegistrationCertificateImage",
              RegistrationCertificateImage,
            );
            ImageData.append(
              "AccreditationCertificateImage",
              AccreditationCertificateImage,
            );
            if (values.attachment?.length > 0) {
              const Images = values.attachment?.map((fileItem: any) =>
                fileItem.getRawFile(),
              );
              Images?.map((image: any) =>
                ImageData.append("OtherImage", image),
              );
            }

            await dispatch(uploadHospitalMultipleImages(ImageData));
          }
          navigate("/hospital");
          // dispatch(getHospitalByID(HospitalID));
        }
      } else {
        const response = await dispatch(createHospital(insertPayload));
        const ID = response.payload.Data.ID;
        if (response?.meta?.requestStatus === "fulfilled" && ID) {
          if (
            (values?.MainImage && typeof values?.MainImage !== "string") ||
            (values?.RegistrationCertificateImage &&
              typeof values?.RegistrationCertificateImage !== "string") ||
            (values?.AccreditationCertificateImage &&
              typeof values?.AccreditationCertificateImage !== "string") ||
            values.attachment?.length > 0
          ) {
            ImageData.append("HospitalID", ID);
            ImageData.append("MainImage", MainImage);
            ImageData.append(
              "RegistrationCertificateImage",
              RegistrationCertificateImage,
            );
            ImageData.append(
              "AccreditationCertificateImage",
              AccreditationCertificateImage,
            );

            if (values.attachment?.length > 0) {
              const Images = values.attachment?.map((fileItem: any) =>
                fileItem.getRawFile(),
              );
              Images?.map((image: any) =>
                ImageData.append("OtherImage", image),
              );
            }

            await dispatch(uploadHospitalMultipleImages(ImageData));
          }
          navigate("/hospital/edit", {
            state: { HospitalID: ID },
          });
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("Url", { value: slug });
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={HospitalDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <StateChangeWatcher formRenderProps={formRenderProps} />
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 65 }}
                gap={{ rows: 0, cols: 20 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                {/* Header */}
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {HospitalID ? "Edit Hospital" : "Add New Hospital"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "flex-start",
                    gap: "10px",
                  }}
                >
                  <ButtonWithLoading
                    label={HospitalID ? "Update Hospital" : "Save Hospital"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    onClick={() => navigate("/hospital")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>

                {/* Basic Information Section */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginTop: 20,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Basic Information
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="HospitalName"
                    label="Hospital Name"
                    placeholder="Enter hospital name"
                    onChange={(e) => handleCreateSlug(e, formRenderProps)}
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Url"
                    label="URL"
                    placeholder="seo-friendly-url"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="TagLine"
                    label="Tag Line"
                    placeholder="e.g., Excellence in Healthcare"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="HospitalTypeID"
                    label="Hospital Type"
                    placeholder="Select Hospital Type"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={HospitalTypeList?.map((city: any) => ({
                      value: city.ID,
                      label: city.Title,
                    }))}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="RegistrationNo"
                    label="Registration No"
                    placeholder="e.g., 00000"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={4}>
                  <Field
                    name="AboutHospital"
                    label="About Hospital"
                    placeholder="Detailed description about the hospital"
                    component={FormRichTextField}
                    rows={5}
                  />
                </GridLayoutItem>

                {/* Location Information */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Location Information
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="Address"
                    label="Address"
                    placeholder="Full hospital address"
                    component={FormTextArea}
                    rows={3}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="Area"
                    label="Area / Locality"
                    placeholder="Area name"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="CityID"
                    label="City"
                    placeholder="Select city"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={cities.map((city: any) => ({
                      value: city.ID,
                      label: city.CityName,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="StateID"
                    label="State"
                    placeholder="Select state"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={states.map((state: any) => ({
                      value: state.ID,
                      label: state.StateName,
                    }))}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={4}>
                  <Field
                    name="MapURL"
                    label="Map URL"
                    placeholder="Map URL"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                {/* Contact Information */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Contact Information
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="MobileNo"
                    label="Mobile Number"
                    placeholder="10-digit mobile number"
                    component={FormTextField}
                    validator={phoneValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="EmailID"
                    label="Email ID"
                    placeholder="hospital@example.com"
                    component={FormTextField}
                    validator={emailWOValueValidator}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Website"
                    label="Website"
                    placeholder="https://www.example.com"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem className="d-flex gap-2" colSpan={4}>
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    avatarStyle={{ margin: 0 }}
                    justifyContent="start"
                    name="MainImage"
                    label="Main Image"
                    alternateName="Hospital Image"
                    component={FormImageUpload}
                    AvatarHeightWidth={70}
                  />
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    avatarStyle={{ margin: 0 }}
                    justifyContent="start"
                    name="RegistrationCertificateImage"
                    label="Registration Certificate Image"
                    alternateName="Registration Certificate Image"
                    component={FormImageUpload}
                    AvatarHeightWidth={70}
                  />
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    avatarStyle={{ margin: 0 }}
                    justifyContent="start"
                    name="AccreditationCertificateImage"
                    label="Accreditation Certificate Image"
                    alternateName="Accreditation Certificate Image"
                    component={FormImageUpload}
                    AvatarHeightWidth={70}
                  />
                </GridLayoutItem>

                {/* Infrastructure & Capacity */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Infrastructure & Capacity
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="EstablishedYear"
                    label="Established Year"
                    placeholder="YYYY"
                    component={FormTextField}
                    type="number"
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="TotalBed"
                    label="Total Beds"
                    placeholder="Number of beds"
                    component={FormTextField}
                    type="number"
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="TotalDoctors"
                    label="Total Doctors"
                    placeholder="Number of doctors"
                    component={FormTextField}
                    type="number"
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="OPDTime"
                    label="OPD Timings"
                    placeholder="e.g., 9:00 AM - 6:00 PM"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    width: "100%",
                    margin: "18px 0px 0px",
                    padding: "0px 10px 8px",
                    fontWeight: 700,
                    background: "whitesmoke",
                    borderRadius: 5,
                    height: 48,
                  }}
                >
                  <Field
                    name="IsEmergencyAvailable"
                    label="24/7 Emergency Available"
                    component={FormCheckbox}
                  />
                </GridLayoutItem>

                {/* Services & Specialties */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Services & Specialties
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="AvailableSevice"
                    label="Available Services"
                    placeholder="Select services"
                    component={FormMultiSelectionFiled}
                    options={services.map((service: any) => ({
                      value: service.ID,
                      label: service.Title,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Specialties"
                    label="Specialties"
                    placeholder="Select specialties"
                    component={FormMultiSelectionFiled}
                    options={specialties.map((specialty: any) => ({
                      value: specialty.ID,
                      label: specialty.Title,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Facilities"
                    label="Facilities"
                    placeholder="Select facilities"
                    component={FormMultiSelectionFiled}
                    options={facilities.map((facility: any) => ({
                      value: facility.ID,
                      label: facility.Title,
                    }))}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Accreditations"
                    label="Accreditations"
                    placeholder="Accreditations"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                {/* About Hospital */}
                {/* <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      About Hospital
                    </Typography.h5>
                  </div>
                </GridLayoutItem> */}

                {/* Status */}
                <GridLayoutItem colSpan={1}>
                  <Field
                    name="IsActive"
                    label="Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY.map((status: any) => ({
                      value: status.value,
                      label: status.label,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={3}
                  style={{
                    marginTop: "20px",
                    textAlign: "end",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    paddingTop: 12,
                    marginBlock: 20,
                  }}
                >
                  <ButtonWithLoading
                    label={HospitalID ? "Update Hospital" : "Save Hospital"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    onClick={() => navigate("/hospital")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            <HospitalImageUpload formRenderProps={formRenderProps} />
          </FormElement>
        )}
      />
      {HospitalID && <HospitalRatingsArray />}
    </>
  );
};

export default CreateHospital;
